import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import SEO from "@/components/SEO";
import { api } from "@/lib/config";
import { useT, useLanguage } from "@/lib/i18n";
import NewsletterForm from "@/components/NewsletterForm";
import { ArrowUpRight } from "lucide-react";

export default function Blog() {
  const t = useT();
  const { lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog")
      .then(({ data }) => setPosts(data))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", {
        day: "numeric", month: "long", year: "numeric",
      });
    } catch { return ""; }
  };

  const scrollTo = (slug) => {
    const el = document.getElementById(`post-${slug}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Layout headerVariant="dark">
      <SEO
        title="Le carnet · Réflexions sur le leadership et le fonctionnement collectif"
        description="Convictions, retours d'expérience et lectures du terrain sur le leadership, le fonctionnement collectif, la transformation et la structuration des organisations."
        path="/blog"
      />
      <section data-testid="blog-hero" className="bg-brand-deep text-white pt-40 md:pt-48 pb-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="section-num text-white/85 fade-up">{t("blog.eyebrow")}</p>
          <h1 className="display text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl fade-up-2">
            {t("blog.title")}
          </h1>
          <p className="text-white/90 text-lg md:text-xl leading-relaxed mt-8 max-w-3xl fade-up-3">
            {t("blog.lede")}
          </p>
        </div>
        <GradientStrip />
      </section>

      <section className="bg-paper-2 py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          {loading && <p className="text-ink/70">{t("common.loading")}</p>}
          {!loading && posts.length === 0 && (
            <p className="text-ink/70">{t("common.emptyBlog")}</p>
          )}

          {!loading && posts.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

              {/* Posts column — LinkedIn feed style */}
              <div className="lg:col-span-8 space-y-10">
                {posts.map((p) => (
                  <article
                    key={p.id}
                    id={`post-${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="bg-paper border border-ink/10 shadow-sm overflow-hidden scroll-mt-32"
                  >
                    {/* Author header */}
                    <div className="flex items-center gap-3 px-6 pt-5">
                      <div className="w-11 h-11 rounded-full bg-brand-deep overflow-hidden shrink-0">
                        <img
                          src="/brand/william_portrait.jpg"
                          alt="William Marques"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="display text-[15px] leading-tight">{p.author || "William Marques"}</p>
                        <p className="text-[12px] text-ink/70 truncate">Fondateur · Wedo Coaching</p>
                        <p className="text-[11px] text-ink/60">{formatDate(p.published_at)} · 🌐</p>
                      </div>
                    </div>

                    {/* Title */}
                    <Link to={`/blog/${p.slug}`} className="block px-6 pt-4 pb-3 group">
                      <h2 className="display text-2xl md:text-3xl leading-tight group-hover:text-brand transition-colors">
                        {p.title}
                      </h2>
                    </Link>

                    {/* Excerpt */}
                    {p.excerpt && (
                      <p className="px-6 pb-4 text-[15px] leading-relaxed text-ink/85 whitespace-pre-line">
                        {p.excerpt}
                      </p>
                    )}

                    {/* Cover image — full width, full height */}
                    {p.cover_image && (
                      <Link to={`/blog/${p.slug}`} className="block bg-paper-2">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          className="w-full max-h-[680px] object-contain"
                        />
                      </Link>
                    )}

                    {/* Tags */}
                    {p.tags && p.tags.length > 0 && (
                      <div className="px-6 pt-4 flex items-center gap-2 flex-wrap">
                        {p.tags.slice(0, 4).map((tag) => (
                          <span
                            key={tag}
                            className="text-[11px] uppercase tracking-wider text-brand border border-brand/30 px-2.5 py-1"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Bottom action bar */}
                    <div className="mt-4 px-6 py-4 border-t border-ink/10 flex items-center justify-end">
                      <Link
                        to={`/blog/${p.slug}`}
                        data-testid={`blog-read-${p.slug}`}
                        className="inline-flex items-center gap-1.5 text-[13px] uppercase tracking-wider font-bold text-brand hover:text-accent-pink transition-colors"
                      >
                        {t("common.cta.readMore")} <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              {/* Sidebar TOC */}
              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28 bg-paper border border-ink/10 shadow-sm">
                  <div className="px-6 py-5 border-b border-ink/10 bg-brand-deep text-white">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-accent-pink">{t("blog.tocEyebrow")}</p>
                    <p className="display text-xl mt-1">{t("blog.tocTitle")}</p>
                  </div>
                  <ul className="divide-y divide-ink/10">
                    {posts.map((p, i) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => scrollTo(p.slug)}
                          data-testid={`toc-${p.slug}`}
                          className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-paper-2 transition-colors group"
                        >
                          <span className="display italic text-accent-pink text-sm w-7 shrink-0 mt-0.5">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] leading-snug text-ink group-hover:text-brand transition-colors line-clamp-3">
                              {p.title}
                            </p>
                            <p className="text-[11px] text-ink/60 mt-1.5 uppercase tracking-wider">
                              {formatDate(p.published_at)}
                            </p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div className="p-6 border-t border-ink/10 bg-paper-2">
                    <p className="text-[13px] leading-relaxed text-ink/85">
                      {t("blog.tocFooter")}
                    </p>
                    <a
                      href="https://www.linkedin.com/in/william-marques-coach/"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[13px] uppercase tracking-wider font-bold text-brand hover:text-accent-pink mt-4"
                    >
                      LinkedIn de William <ArrowUpRight size={14} />
                    </a>
                  </div>

                  <NewsletterForm />
                </div>
              </aside>

            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
